import { HttpClient, HttpErrorResponse } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { AuthService } from '@modules/auth-module/services/auth.service'
import { UserDetailInterface } from '@modules/user-module/interfaces/user-detail.interface'
import { catchError, map, Observable, of, skip, tap } from 'rxjs'
import { EnvironmentsHelperService } from '@environments/core/services/environments-helper.service'
import { WebsocketService } from '@modules/websocket-module/services/websocket.service'
import { UserHelperService } from './user-helper.service'
import { TokensInterface } from '@modules/auth-module/interfaces/tokens.interface'
import { UserDetailSocketRequest } from '../interfaces/user-detail-socket-request.interface'
import { UserDetailSocketType } from '../types/user-detail-socket.type'
import { WebSettingsService } from '@modules/user-module/services/web-settings.service'
import { WebSettingsInterface } from '@modules/user-module/interfaces/web-settings.interface'

@Injectable({
	providedIn: 'root',
})
export class UserService {
	private readonly _http: HttpClient = inject<HttpClient>(HttpClient)
	private readonly _authService: AuthService = inject<AuthService>(AuthService)
	private readonly _userHelperService: UserHelperService = inject<UserHelperService>(UserHelperService)
	private readonly _websocketService: WebsocketService<UserDetailSocketRequest, UserDetailSocketType> =
		inject<WebsocketService<UserDetailSocketRequest, UserDetailSocketType>>(WebsocketService)
	private readonly _environmentsHelperService: EnvironmentsHelperService =
		inject<EnvironmentsHelperService>(EnvironmentsHelperService)
	private readonly _webSettingsService: WebSettingsService = inject<WebSettingsService>(WebSettingsService)

	public getUserDetail(): Observable<UserDetailInterface> {
		const user: UserDetailInterface | null = this._userHelperService.userDetail$.getValue()

		if (this._userHelperService.isWorkingUserSocket && user) return of(user)

		if (!user) this._userHelperService.isLoadingUserDetail$.next(true)

		return this._http
			.get<UserDetailInterface>(`${this._environmentsHelperService.apiUrl}/user_info/user_details/`)
			.pipe(
				tap((response: UserDetailInterface): void => {
					this._userHelperService.userDetail$.next(response)
					this._userHelperService.isLoadingUserDetail$.next(false)
				}),
				catchError((error: HttpErrorResponse): never => {
					this._userHelperService.isLoadingUserDetail$.next(false)
					throw error
				}),
			)
	}

	public launchSocketUserDetail(): Observable<UserDetailInterface | null> {
		const tokens: TokensInterface | null = this._authService.tokens
		if (!tokens) return of(null)

		const url: string = `${this._environmentsHelperService.socketUrl}/ws-user-detail/user-detail`

		this._websocketService.connect(url)

		if (!this._websocketService.hasMessageInSocket(url)) {
			this._websocketService.sendMessage(url, {
				action: 'auth',
				params: tokens.access,
			})
		}

		return this._websocketService.getResponses(url).pipe(
			skip(2), // пропускаем 2 служебных ответа
			map((response: UserDetailSocketType): UserDetailInterface | null => {
				const userDetail: UserDetailInterface | null = this._userHelperService.userDetail$.getValue()
				return userDetail ? { ...userDetail, ...response } : null
			}),
			tap((userDetail: UserDetailInterface | null): void => {
				this._userHelperService.toggleWorkingUserSocket(true)
				if (!userDetail) return
				this._userHelperService.userDetail$.next(userDetail)
				this._userHelperService.isLoadingUserDetail$.next(false)
			}),
			catchError((error: HttpErrorResponse): never => {
				this._userHelperService.toggleWorkingUserSocket(false)
				this._userHelperService.isLoadingUserDetail$.next(false)
				throw error
			}),
		)
	}

	public updateUserWebSettings(data?: WebSettingsInterface | undefined): Observable<WebSettingsInterface | null> {
		const tokens: TokensInterface | null = this._authService.tokens
		if (!tokens) return of(null)

		const webSettings: WebSettingsInterface | null = data ?? this._webSettingsService.collectedWebSettings
		if (!webSettings) return of(null)

		return this._http
			.put<WebSettingsInterface>(`${this._environmentsHelperService.apiUrl}/ss/update_user_web_settings/`, {
				web_settings: webSettings,
			})
			.pipe(
				tap((): void => {
					this._userHelperService.updateUserWebSettings(webSettings)
				}),
			)
	}
}
